import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { getContactSubmissions, markContactAsRead } from '../../lib/supabase';

const ContactsManager: React.FC = () => {
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await getContactSubmissions();
      setContacts(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markContactAsRead(id);
      setContacts(
        contacts.map((c) => (c.id === id ? { ...c, is_read: true } : c))
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Impact", "Arial Black", sans-serif',
            fontWeight: 900,
            fontSize: '2rem',
            textTransform: 'uppercase',
            color: '#1a1a1a',
          }}
        >
          Contact Submissions
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 0,
            border: '2px solid #dc143c',
          }}
        >
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 0, border: '2px solid #dc143c', bgcolor: 'white' }}>
        <Table sx={{ bgcolor: 'white' }}>
          <TableHead sx={{ bgcolor: '#1a1a1a' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Message</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontFamily: '"Arial Black", sans-serif', textTransform: 'uppercase' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: 'white' }}>
            {contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No contact submissions yet
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  sx={{
                    bgcolor: contact.is_read ? 'transparent' : '#fff3cd',
                    '&:hover': {
                      bgcolor: contact.is_read ? '#f5f5f5' : '#ffe69c',
                    }
                  }}
                >
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>{formatDate(contact.created_at)}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif', fontWeight: 600 }}>{contact.full_name}</TableCell>
                  <TableCell sx={{ fontFamily: '"Arial", sans-serif' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" />
                      {contact.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contact.submission_type || 'General'}
                      size="small"
                      sx={{
                        borderRadius: 0,
                        bgcolor: contact.submission_type === 'speaking' ? '#dc143c' : contact.submission_type === 'collaboration' ? '#1a1a1a' : '#666',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, fontFamily: '"Arial", sans-serif' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {contact.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {contact.is_read ? (
                      <Chip label="Read" size="small" color="success" />
                    ) : (
                      <Chip label="Unread" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    {!contact.is_read && (
                      <IconButton
                        size="small"
                        onClick={() => handleMarkAsRead(contact.id)}
                        title="Mark as read"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ContactsManager;